import {
  faCog,
  faCopy,
  faFolder,
  faFolderOpen,
  faLanguage,
  faShareNodes,
  faUsers,
} from '@fortawesome/pro-regular-svg-icons';
import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Dispatch, SetStateAction, SyntheticEvent, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';
import { ProjectGq } from '../../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';
import { CurrentUserGq } from '../../../../graphql/users/models/CurrentUserGq';
import { ProjectTabChoices } from '../../../../pages/ProjectPage/ProjectPage';
import { ProjectSettingsMenuChoices } from '../../../../pages/ProjectSettingsPage/ProjectSettingsPage';
import VoxelIconButton from '../../../voxel/Buttons/VoxelIconButton';
import { VoxelPopoverContent } from '../../../voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../voxel/Popover/VoxelPopoverTrigger';
import { VoxelPopover } from '../../../voxel/Popover/VoxelPopover';
import VoxelMenuItemContainer from '../../../voxel/PopoverMenu/VoxelMenuItemContainer';
import VoxelPopoverMenuItem from '../../../voxel/PopoverMenu/VoxelPopoverMenuItem';
import VoxelDivider from '../../../voxel/VoxelDivider';

export interface ProjectOptionsButtonProps {
  project: ProjectGq;
  setShowTranslateProgressId?: (id: number) => void;
  translateProject?: (id: ProjectGq) => void;
  setShowCopyProgressId?: (id: number) => void;
  copyProject?: (id: ProjectGq) => void;
  user?: CurrentUserGq;
  archived: boolean;
  setArchived: Dispatch<SetStateAction<boolean>>;
}

export const ProjectOptionsButton: React.FC<ProjectOptionsButtonProps> = ({
  project,
  copyProject,
  translateProject,
  user,
  archived,
  setArchived,
}) => {
  // Navigate
  const history = useHistory();

  // Handle copy
  const handleCopyProject = useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation();
      event.preventDefault();
      copyProject(project);
    },
    [project]
  );

  // Handle translate
  const handleTranslateProject = useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation();
      event.preventDefault();
      translateProject(project);
    },
    [project]
  );

  const canArchive = useMemo(() => {
    return project.accessUserCanArchive;
  }, [project, user]);

  // Archive action
  const archiveMutation = projectsApiService.mutations.projects.useArchiveProject();
  const handleArchive = useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation();
      event.preventDefault();
      const newIsArchived = !archived;
      setArchived(newIsArchived);
      archiveMutation({
        id: project.id,
        projectIsArchived: newIsArchived,
      });
    },
    [project.archivedTime, archived]
  );

  return (
    <VoxelPopover>
      <VoxelPopoverTrigger enableActive>
        <VoxelIconButton
          size="sm"
          color="white"
          icon={<FontAwesomeIcon icon={faEllipsisVertical} size="lg" />}
        />
      </VoxelPopoverTrigger>
      <VoxelPopoverContent>
        <VoxelMenuItemContainer size="md">
          <VoxelPopoverMenuItem
            onClick={() =>
              history.push(
                `${project.id}/${ProjectTabChoices.Settings}/${ProjectSettingsMenuChoices.General}`
              )
            }
            title={'Settings'}
            icon={<FontAwesomeIcon icon={faCog} />}
          />
          <VoxelPopoverMenuItem
            onClick={() =>
              history.push(
                `${project.id}/${ProjectTabChoices.Settings}/${ProjectSettingsMenuChoices.TeamAccess}`
              )
            }
            title={'Team Access'}
            icon={<FontAwesomeIcon icon={faShareNodes} />}
          />
        </VoxelMenuItemContainer>
        <VoxelDivider />
        <VoxelMenuItemContainer>
          <VoxelPopoverMenuItem
            onClick={(event) => handleCopyProject(event)}
            title={'Copy'}
            icon={<FontAwesomeIcon icon={faCopy} />}
            disabled={
              project.copyInProgress ||
              project.translateInProgress ||
              !user.accessUserCanCreateProject
            }
          />
          <VoxelPopoverMenuItem
            onClick={(event) => handleTranslateProject(event)}
            title={'Translate'}
            icon={<FontAwesomeIcon icon={faLanguage} />}
            disabled={
              project?.copyInProgress ||
              project?.translateInProgress ||
              !user?.accessUserCanCreateProject
            }
          />
          <VoxelPopoverMenuItem
            onClick={(event) => handleArchive(event)}
            title={archived ? 'Unarchive' : 'Archive'}
            icon={<FontAwesomeIcon icon={archived ? faFolderOpen : faFolder} />}
            disabled={project.copyInProgress || project.translateInProgress || !canArchive}
          />
        </VoxelMenuItemContainer>
      </VoxelPopoverContent>
    </VoxelPopover>

    /*     ({ TransitionProps, placement }) => (
        <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
        >
        </Grow> */
  );
};

export default ProjectOptionsButton;
